import React from 'react';
import { motion } from 'framer-motion';
import { useAuthContext } from '../components/auth/AuthProvider';
import { supabase } from '../utils/supabase';
import { toast } from 'react-hot-toast';
import {
  User, Shield, Award, Zap, Clock, DollarSign,
  Github, Twitter, Linkedin, Globe, Settings,
  Bell, ChevronRight, Upload
} from 'lucide-react';
import { Link } from 'react-router-dom';

export function UserDashboard() {
  const { user, profile } = useAuthContext();
  const [isUploading, setIsUploading] = React.useState(false);

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setIsUploading(true);
      const file = event.target.files?.[0];
      if (!file || !user) return;

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const filePath = `avatars/${user.id}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update user profile
      const { error: updateError } = await supabase
        .from('users')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      toast.success('Avatar updated successfully');
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      toast.error(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
            {/* Avatar */}
            <div className="relative">
              <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 p-0.5">
                <div className="w-full h-full rounded-xl overflow-hidden">
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={profile.display_name || ''}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-blue-50 flex items-center justify-center">
                      <User className="w-12 h-12 text-blue-500" />
                    </div>
                  )}
                </div>
              </div>
              <label className="absolute -bottom-2 -right-2 p-2 bg-white rounded-full shadow-lg cursor-pointer hover:scale-110 transition-transform">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  disabled={isUploading}
                />
                <Upload className="w-4 h-4 text-gray-500" />
              </label>
            </div>

            {/* User Info */}
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  {profile?.display_name || user?.email}
                </h1>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  profile?.kyc_status === 'approved' ? 'bg-green-100 text-green-800' :
                  profile?.kyc_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {profile?.kyc_status ? `KYC ${profile.kyc_status}` : 'KYC Required'}
                </div>
              </div>
              <p className="text-gray-500 mb-4">{profile?.bio || 'No bio yet'}</p>
              <div className="flex items-center gap-4">
                {profile?.social_links?.github && (
                  <a href={profile.social_links.github} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-700">
                    <Github className="w-5 h-5" />
                  </a>
                )}
                {profile?.social_links?.twitter && (
                  <a href={profile.social_links.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-700">
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
                {profile?.social_links?.linkedin && (
                  <a href={profile.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-700">
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
                {profile?.social_links?.website && (
                  <a href={profile.social_links.website} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-700">
                    <Globe className="w-5 h-5" />
                  </a>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-col gap-2">
              <Link
                to="/settings"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Settings className="w-4 h-4" />
                Settings
              </Link>
              <button className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                <Bell className="w-4 h-4" />
                Notifications
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Zap className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Hashrate</p>
                <p className="text-xl font-bold text-gray-900">
                  {profile?.mining_stats?.total_hashrate || '0'} TH/s
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Earnings</p>
                <p className="text-xl font-bold text-gray-900">
                  ${profile?.mining_stats?.total_earnings?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <Clock className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Uptime</p>
                <p className="text-xl font-bold text-gray-900">
                  {profile?.mining_stats?.uptime?.toFixed(2) || '0.00'}%
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-50 rounded-lg">
                <Award className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Level</p>
                <p className="text-xl font-bold text-gray-900">
                  {profile?.achievements?.level || 1}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* KYC Status */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                KYC Status
              </h2>
              {profile?.kyc_status !== 'approved' && (
                <Link
                  to="/settings?tab=kyc"
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  Complete KYC
                  <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>

            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${
                profile?.kyc_status === 'approved' ? 'bg-green-50' :
                profile?.kyc_status === 'pending' ? 'bg-yellow-50' :
                'bg-gray-50'
              }`}>
                <div className="flex items-center gap-3 mb-2">
                  <Shield className={`w-5 h-5 ${
                    profile?.kyc_status === 'approved' ? 'text-green-500' :
                    profile?.kyc_status === 'pending' ? 'text-yellow-500' :
                    'text-gray-500'
                  }`} />
                  <span className="font-medium text-gray-900">
                    {profile?.kyc_status ? `KYC ${profile.kyc_status}` : 'KYC Required'}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {profile?.kyc_status === 'approved' ? 'Your identity has been verified.' :
                   profile?.kyc_status === 'pending' ? 'Your documents are being reviewed.' :
                   'Please complete KYC verification to unlock all features.'}
                </p>
              </div>

              {profile?.kyc_status === 'approved' && profile.kyc_verified_at && (
                <div className="text-sm text-gray-500">
                  Verified on: {new Date(profile.kyc_verified_at).toLocaleDateString()}
                </div>
              )}
            </div>
          </motion.div>

          {/* Achievements */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-6">
              <Award className="w-5 h-5 text-orange-500" />
              Achievements
            </h2>

            <div className="space-y-4">
              {profile?.achievements?.badges?.map((badge: string, index: number) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                  <Award className="w-5 h-5 text-orange-500" />
                  <span className="text-sm font-medium text-gray-900">{badge}</span>
                </div>
              ))}
              {(!profile?.achievements?.badges || profile.achievements.badges.length === 0) && (
                <div className="text-sm text-gray-500">
                  No achievements yet. Start mining to earn badges!
                </div>
              )}
            </div>
          </motion.div>

          {/* Mining Stats */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-6">
              <Zap className="w-5 h-5 text-blue-500" />
              Mining Activity
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500">Active Miners</span>
                <span className="text-sm font-medium text-gray-900">
                  {profile?.mining_stats?.active_miners || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500">24h Earnings</span>
                <span className="text-sm font-medium text-gray-900">
                  ${profile?.mining_stats?.earnings_24h?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500">Efficiency</span>
                <span className="text-sm font-medium text-gray-900">
                  {profile?.mining_stats?.efficiency?.toFixed(2) || '0.00'} TH/W
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}